import { useEffect, useRef, useState } from 'react';
import { Check, ChevronDown, User as UserIcon } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useT, type Lang } from '../i18n';
import { api } from '../api/client';
import { GithubAccount } from './GithubAccount';

/**
 * User tag in the topbar, expanded into a small profile dropdown: shows the
 * user's name/email, a link to the full profile page, and the app-language
 * choice. Language changes apply immediately and are persisted server-side.
 */
export function ProfileMenu({ onOpenProfile }: { onOpenProfile: () => void }) {
  const { user } = useAuth();
  const { t, lang, setLang, languages } = useT();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const chooseLang = (next: Lang) => {
    setLang(next);                                        // apply immediately (also persisted in localStorage)
    api.updateProfileLang(next).catch(() => {});          // persist server-side; UI already updated
  };

  if (!user) return null;

  return (
    <div className="profile-menu" ref={rootRef}>
      <button
        type="button"
        className="user-tag profile-menu-trigger"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t('profile.menuAria')}
        title={t('profile.title')}
      >
        <div className="user-initial">{user.initials}</div>
        <span>{user.name}</span>
        <ChevronDown size={12} className={`profile-menu-chevron ${open ? 'profile-menu-chevron--open' : ''}`} />
      </button>

      {open && (
        <div className="profile-menu-panel" role="menu">
          <div className="profile-menu-header">
            <div className="profile-menu-name">{user.name}</div>
            {user.email && <div className="profile-menu-email">{user.email}</div>}
          </div>
          <div className="profile-menu-divider" />
          <button
            type="button"
            role="menuitem"
            className="profile-menu-item"
            onClick={() => { setOpen(false); onOpenProfile(); }}
          >
            <span>{t('shell.myProfile')}</span>
            <UserIcon size={13} />
          </button>
          <div className="profile-menu-divider" />
          <div className="profile-menu-section-label">{t('common.language')}</div>
          {languages.map((l) => (
            <button
              key={l.code}
              type="button"
              role="menuitemradio"
              aria-checked={lang === l.code}
              className={`profile-menu-item ${lang === l.code ? 'profile-menu-item--active' : ''}`}
              onClick={() => chooseLang(l.code)}
            >
              <span>{l.label}</span>
              {lang === l.code && <Check size={13} />}
            </button>
          ))}
          <div className="profile-menu-divider" />
          <div className="profile-menu-section-label">GitHub</div>
          <GithubAccount variant="menu" />
        </div>
      )}
    </div>
  );
}
